/**
 * Module dependencies.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Button } from 'app/components/atoms/button/button';
import { useForm } from 'react-hook-form';
import { InputField } from 'app/components/atoms/forms/fields/input/input-field';
import { requiredRule } from 'app/core/utils/field-rules';
import { Pencil } from 'lucide-react';
import { TreeNode } from './file-button';
import { useActionsContent } from './actions-provider';

/**
 * `Props` type.
 */

type Props = {
  node: TreeNode;
};

/**
 * Export `EditName` component.
 */

export function EditName({ node }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { postEditItem } = useActionsContent();
  const formRef = useRef<HTMLFormElement>(null);
  const [open, setOpen] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: {},
    mode: 'all'
  });

  useEffect(() => {
    if (open) {
      reset({ name: node.title });
    }
  }, [node.title, open, reset]);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOk = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        await postEditItem({
          id: node.key,
          name: data.name
        });

        reset();
        setOpen(false);
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    },
    [node.key, postEditItem, reset]
  );

  return (
    <>
      <div className={'text-muted-foreground hover:text-foreground'} onClick={() => setOpen(true)}>
        <Pencil className={'h-4 w-4'} />
      </div>

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('notes.actions.editName')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            <InputField
              control={control}
              label={translate('common.labels.name')}
              name={'name'}
              rules={requiredRule(translate)}
            />
          </form>

          <DialogFooter>
            <Button onClick={handleCancel}>{translate('common.actions.cancel')}</Button>

            <Button loading={isSubmitting} onClick={handleOk} type={'primary'}>
              {translate('common.actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
